import { ChevronRight } from '@material-ui/icons';
import { Box, palette, Stack } from '@solvhealth/jigsaw-v1';
import React from 'react';
import styled from 'styled-components';
import { analyticsTrackEvent } from '../../../../../../../../core/analytics';
import {
  ACCOUNT_HOME_TOUCH_APPOINTMENT_CARD_PAST,
  ACCOUNT_HOME_TOUCH_APPOINTMENT_CARD_UPCOMING,
  VISIT_SUMMARY_TOUCH_DOWNLOAD,
  VISIT_SUMMARY_TOUCH_VIEW,
} from '../../../../../../../../core/analytics/events';
import history from '../../../../../../../../core/history';
import { isDischarged, isUpcoming } from '../../../../../../../../core/util/booking';
import useFetchSingleProvider from '../../../../../../../ProviderGroup/hooks/useFetchSingleProvider';
import AppointmentIcon from './components/AppointmentIcon';
import AppointmentTime from './components/AppointmentTime';
import { ClinicOrProviderName } from './components/ClinicOrProviderName';

import AppointmentCardButton from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/components/AppointmentCardButton';
import { useActiveBookingState } from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/hooks/useActiveBookingState';
import {
  useVisitSummary,
  useVisitSummaryDownloadedMutation,
} from '~/components/AccountGetsMarried/components/GetCare/hooks/useVisitSummary';
import { VisitSummaryStatuses } from '~/components/AccountGetsMarried/constants/visitSummary';
import { useNextOrLastAppointment } from '~/components/AccountGetsMarried/hooks/useNextOrLastAppointment';
import { getLocationName } from '~/components/Account/Profile/VisitCard/Upcoming/util';
import DescriptiveText from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/components/DescriptiveText';
import { ActiveBookingStates } from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/util';

const GreyRight = styled(ChevronRight).withConfig({ componentId: 'sc-1qtddrt-0' })`
  color: ${palette.gray[500]};
`;

const StackWrap = styled(Stack).withConfig({ componentId: 'sc-1qtddrt-1' })`
  display: flex;
  flex-grow: 2;
  justify-content: space-between;
  width: inherit;
`;

type Props = {
  userProfileId: string;
};

export function useShouldHideAppointmentCard(userProfileId: string) {
  const { isFetching, appointment, isLastAppointment } = useNextOrLastAppointment(userProfileId);

  const bookingId = appointment?.id;
  const {
    summaryStatus,
    isLoading: isLoadingVisitSummary,
    summaryExists,
  } = useVisitSummary(bookingId);
  if (!appointment) {
    return true;
  }

  if (isLastAppointment) {
    if (isLoadingVisitSummary) {
      return true;
    }

    if (!summaryExists && summaryStatus !== VisitSummaryStatuses.Pending) {
      // If summary does not exist AND is not possibly being drafted, return null
      return true;
    }
  }

  return false;
}

const AppointmentCard = (props: Props) => {
  const { userProfileId } = props;
  const { appointment } = useNextOrLastAppointment(userProfileId);
  const bookingId = appointment?.id;
  const bookingState = useActiveBookingState(appointment);
  const { provider } = useFetchSingleProvider(appointment?.provider_id);
  const { summaryStatus, isLoading: isLoadingVisitSummary } = useVisitSummary(bookingId);
  const downloadMutation = useVisitSummaryDownloadedMutation(bookingId);
  const isLoading = isLoadingVisitSummary || downloadMutation.isLoading;

  const shouldHide = useShouldHideAppointmentCard(userProfileId);
  if (shouldHide) {
    return null;
  }

  const onAppointmentCardClick = () => {
    const analyticsProperties = {
      location_id: appointment.location_id,
      booking_id: bookingId,
    };
    let eventName;
    if (isUpcoming(appointment)) {
      eventName = ACCOUNT_HOME_TOUCH_APPOINTMENT_CARD_UPCOMING;
    } else {
      eventName = ACCOUNT_HOME_TOUCH_APPOINTMENT_CARD_PAST;
    }
    analyticsTrackEvent(eventName, analyticsProperties);

    if (isDischarged(appointment)) {
      if (
        bookingState === ActiveBookingStates.PostTelemedNoRating ||
        bookingState === ActiveBookingStates.PostClinicVisitNoRating
      ) {
        history.push(`/account/add-review/${bookingId}`);
      } else {
        history.push(`/account/home/${userProfileId}/manage?b=${bookingId}`);
      }
    } else {
      history.push({ pathname: `/pass/${bookingId}` });
    }
  };

  const onAppointmentCardButtonClick = () => {
    switch (summaryStatus) {
      case VisitSummaryStatuses.Downloadable:
      case VisitSummaryStatuses.Updatable:
        analyticsTrackEvent(VISIT_SUMMARY_TOUCH_DOWNLOAD, {
          entrypoint: 'Account home appointment card',
        });
        downloadMutation.mutate();
        return;
      case VisitSummaryStatuses.Viewable:
        analyticsTrackEvent(VISIT_SUMMARY_TOUCH_VIEW, {
          entrypoint: 'Account home appointment card',
        });
        history.push(`/visit-summary/${bookingId}`);
        return;
      default:
        onAppointmentCardClick();
    }
  };

  return (
    <Stack
      bg="white"
      borderRadius="base"
      boxShadow="md"
      margin="auto"
      maxWidth="400px"
      minHeight="150px"
      overflow="hidden"
    >
      <Stack
        borderRadius="none"
        justifyContent="space-between"
        onClick={onAppointmentCardClick}
        width="100%"
      >
        <StackWrap alignItems="center" pt={2} px={2} row space={2}>
          <Stack alignItems="center" overflow="hidden" row space={2}>
            <Box>
              <AppointmentIcon
                appointment={appointment}
                bookingState={bookingState}
                locationId={appointment.location_id}
              />
            </Box>
            <Box overflow="hidden">
              <AppointmentTime appointment={appointment} bookingState={bookingState} />
              <ClinicOrProviderName
                booking={appointment}
                provider={provider}
                textProps={{
                  sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Box>
          </Stack>
          <GreyRight />
        </StackWrap>
      </Stack>
      <Stack p={1.5}>
        <AppointmentCardButton
          bookingState={bookingState}
          disabled={summaryStatus === VisitSummaryStatuses.Pending}
          isLoading={isLoading}
          onAppointmentCardClick={onAppointmentCardButtonClick}
        />
      </Stack>
      <Stack bg="gray-100" px={2} py={0.5}>
        <DescriptiveText
          bookingState={bookingState}
          isLoading={isLoading}
          locationName={getLocationName(appointment)}
          patientName={appointment.first_name}
        />
      </Stack>
    </Stack>
  );
};

export default AppointmentCard;
